/**
 * My Profile API — backend calls for the logged-in user's profile page.
 *
 * Uses the same user-manage endpoints as the admin Users page, but with
 * self-targeted actions:
 *   - "update"     → edit own profile (email, display name, groups)
 *   - "reset-self" → change own password (requires current password)
 *
 * Cloud:  VITE_USER_V2_ENDPOINT (/user)        → apiUserManageCloud
 * Legacy: VITE_USER_ENDPOINT    (/user_manager) → apiUserManageLegacy
 */

import { apiFetch, apiUserManageCloud, apiUserManageLegacy } from '../../../../services/api.js';
import { getCampusConfig, getSession } from '../../../../services/session.js';
import { campusToGuid } from '../../../../libs.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true if the current campus is cloud-enabled */
function isCloud() {
  return !!getCampusConfig()?.is_cloud;
}

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all user groups for a campus (for group name lookup + group editing).
 *
 * Procedure: sp_dev_getgroupsbycampusid
 */
export function fetchUserGroups(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getgroupsbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Edit the logged-in user's own profile.
 *
 * Cloud:  user-manage-2  action: "update"
 * Legacy: user-manage     Action: "update"
 *
 * @param {object} data — { email, display_name?, group_ids? }
 * @param {object} session — { campusId, userId, clientId, username, roleId, cognitoId,
 *                              displayName, email: currentEmail, groups, displayEdit, groupEdit, migrated }
 * @returns {Promise<[boolean, string?]>}
 */
export function editSelfProfile(data, session) {
  if (isCloud()) {
    const payload = [{
      action: 'update',
      username: session.username,
      user_id: session.userId,
      campus_id: session.campusId,
      email: data.email.toLowerCase(),
      modified_by: session.userId,
      user_role_id: session.roleId,
      cognito_id: session.cognitoId,
      display_name: session.displayEdit ? data.display_name : session.displayName,
      group_ids: session.groupEdit ? (data.group_ids || []) : (session.groups || []),
      oldPayload: {
        username: session.username,
        user_id: session.userId,
        campus_id: session.campusId,
        email: session.email,
        user_role_id: session.roleId,
        cognito_id: session.cognitoId,
        display_name: session.displayName,
        group_ids: session.groups || [],
      },
    }];
    return apiUserManageCloud({
      payload,
      clientId: session.clientId,
      userId: session.userId,
    });
  }

  // Legacy
  const payload = [{
    Action: 'update',
    Username: session.username,
    UserId: session.userId,
    CampusId: session.campusId,
    UserEmail: data.email.toLowerCase(),
    Migrated: session.migrated || false,
    ClientId: session.clientId,
    CampusGuid: campusToGuid(getSession(), session.campusId),
    UserRole: session.roleId,
    CognitoId: session.cognitoId,
    DisplayName: session.displayEdit ? data.display_name : session.displayName,
    GroupIds: session.groupEdit ? (data.group_ids || []) : (session.groups || []),
  }];
  return apiUserManageLegacy({
    payload,
    userId: session.userId,
  });
}

/**
 * Reset the logged-in user's own password.
 *
 * Requires current password verification (action: "reset-self").
 *
 * Cloud:  user-manage-2  action: "reset-self"
 * Legacy: user-manage     Action: "reset-self"
 *
 * @param {object} data — { currentPassword, password }
 * @param {object} session — { campusId, userId, clientId, username, migrated }
 * @returns {Promise<[boolean, string?]>}
 */
export function resetSelfPassword(data, session) {
  if (isCloud()) {
    const payload = [{
      action: 'reset-self',
      campus_id: session.campusId,
      username: session.username,
      password: data.password,
      orig_pass: data.currentPassword,
      user_id: session.userId,
      modified_by: session.userId,
      origPayload: {},
    }];
    return apiUserManageCloud({
      payload,
      clientId: session.clientId,
      userId: session.userId,
    });
  }

  // Legacy
  const payload = [{
    Action: 'reset-self',
    CampusId: session.campusId,
    Username: session.username,
    UserPass: data.password,
    OrigPass: data.currentPassword,
    Migrated: session.migrated || false,
    ClientId: session.clientId,
  }];
  return apiUserManageLegacy({
    payload,
    userId: session.userId,
  });
}
